// src/hooks/useAppConfig.js
import { useState, useMemo, useEffect } from "react";
import { getParamsFromUrl, generateRandomChannelName } from "../utils/agoraUtils";
import { NativeBridge } from "../utils/nativeBridge";
import logger from "../utils/logger";

export const useAppConfig = () => {
  const urlParams = useMemo(() => getParamsFromUrl(), []);
  const nativeBridge = useMemo(() => new NativeBridge(), []);

  const [agoraConfig, setAgoraConfig] = useState(() => ({
    appId: process.env.REACT_APP_AGORA_APP_ID,
    channelName: urlParams.channelName ?? process.env.REACT_APP_AGORA_CHANNEL_NAME,
    token: process.env.REACT_APP_AGORA_TOKEN || null,
    uid: process.env.REACT_APP_AGORA_UID || null,
    voice_id: urlParams.voice_id || null,
    prompt: urlParams.prompt || null,
    greeting: urlParams.greeting || null,
    profile: urlParams.profile || null,
    name: urlParams.name || null, // Add name parameter
    endpoint: urlParams.endpoint  ?? process.env.REACT_APP_AGENT_ENDPOINT,
  }));

  const [trulienceConfig, setTrulienceConfig] = useState(() => ({
    avatarId: urlParams.avatarId ?? process.env.REACT_APP_TRULIENCE_AVATAR_ID,
    trulienceSDK: urlParams.trulienceSdkURL ?? process.env.REACT_APP_TRULIENCE_SDK_URL,
    avatarToken: process.env.REACT_APP_TRULIENCE_AVATAR_TOKEN || null,
    // Optional override so the Trulience SDK targets a specific (test) media
    // server instead of the one returned by auth. Consumed via envParams in
    // AvatarView. Falls back to the value the agent response supplies later.
    controllerEndpoint: urlParams.controllerEndpoint ?? null,
  }));

  const [controllerEndpoint, setControllerEndpoint] = useState(urlParams.controllerEndpoint ?? "")

  const derivedChannelName = useMemo(() => {
    if (agoraConfig.channelName === "random") {
      return generateRandomChannelName();
    }
    return agoraConfig.channelName;
  }, [agoraConfig.channelName]);

  // Debugging logs
  useEffect(() => {
    if (!process.env.REACT_APP_AGORA_APP_ID) {
      logger.error(
        "Missing Agora App ID. Set REACT_APP_AGORA_APP_ID in your .env file"
      );
    }
    logger.log("URL Parameters:", urlParams);
    logger.log("Continue param:", urlParams.continue);
    logger.log("Content params:", {
      type: urlParams.contentType,
      url: urlParams.contentURL,
      alt: urlParams.contentALT
    });
  }, [urlParams]);

  useEffect(() => {
    const handleAgoraDetailsUpdated = (data) => {
      const { appId, channelName, uid, voice_id, prompt, greeting, profile, name, endpoint } = data;
      setAgoraConfig(_agoraConfig => ({
        ..._agoraConfig,
        appId,
        channelName,
        uid,
        voice_id,
        prompt,
        greeting,
        profile,
        name, // Add name parameter
        endpoint
      }));
    };

    const handleTrulienceDetailsUpdated = ({ avatarId }) => {
      setTrulienceConfig(config => ({ ...config, avatarId }));
    };

    if (nativeBridge) {
      nativeBridge.on("agoraDetailsUpdated", handleAgoraDetailsUpdated);
      nativeBridge.on("trulienceDetailsUpdated", handleTrulienceDetailsUpdated);
    }

    return () => {
      if (nativeBridge) {
        nativeBridge.off("agoraDetailsUpdated", handleAgoraDetailsUpdated);
        nativeBridge.off("trulienceDetailsUpdated", handleTrulienceDetailsUpdated);
      }
    };
  }, [nativeBridge]);

  return {
    urlParams,
    agoraConfig,
    setAgoraConfig,
    trulienceConfig,
    derivedChannelName,
    setControllerEndpoint,
    controllerEndpoint
  };
};